import React from 'react';
import { X, AlertTriangle, Eye, ArrowRight, UserCheck } from 'lucide-react';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { useNavigate } from 'react-router-dom';

const DuplicateWarningModal = ({ isOpen, onClose, duplicates, onContinue, onUseExisting }) => {
  const { safeT } = useTranslationReady();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getDisplayName = (customer) => {
    const parts = [];
    if (customer.FirstName) parts.push(customer.FirstName);
    if (customer.LastName) parts.push(customer.LastName);
    if (parts.length === 0 && customer.FirstName_Eng) parts.push(customer.FirstName_Eng);
    if (parts.length === 0 && customer.LastName_Eng) parts.push(customer.LastName_Eng);
    return parts.join(' ') || safeT('common.unnamed', 'Unnamed');
  };

  const getFieldValue = (customer, ...fieldNames) => {
    for (const field of fieldNames) {
      if (customer[field] && String(customer[field]).trim() !== '') {
        return customer[field];
      }
    }
    return '-';
  };

  const handleViewCustomer = (customerId) => {
    onClose();
    navigate(`/customers/${customerId}`);
  };

  const handleUseExisting = (customer) => {
    if (onUseExisting) {
      onUseExisting(customer);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {safeT('customers.duplicateEmailWarning', 'Duplicate Email Found')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Warning Message */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {safeT('customers.duplicateEmailMessage', `This email address already exists in ${duplicates.length} other customer record${duplicates.length > 1 ? 's' : ''}. You can:`)}
            </p>
            <ul className="mt-2 text-sm text-amber-700 dark:text-amber-300 list-disc list-inside space-y-1">
              <li>{safeT('customers.duplicateOption1', 'Continue and create a new customer anyway')}</li>
              <li>{safeT('customers.duplicateOption2', 'Update an existing customer record with the new data')}</li>
              <li>{safeT('customers.duplicateOption3', 'View the existing customer(s) to review them first')}</li>
            </ul>
          </div>

          {/* List of Duplicate Customers */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {safeT('customers.existingCustomers', 'Existing Customers with this Email')}
            </h3>
            <div className="space-y-3">
              {duplicates.map((customer) => (
                <div
                  key={customer.CustomerID}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {getDisplayName(customer)}
                        </h4>
                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                          {customer.CustomerCode}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <span>📧</span>
                          <span>{getFieldValue(customer, 'Email', 'PersonEmail')}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span>📞</span>
                          <span>{getFieldValue(customer, 'Phone', 'PersonPhone')}</span>
                        </div>
                        {(customer.JobTitle || customer.Department) && (
                          <div className="flex items-center space-x-2">
                            <span>💼</span>
                            <span>
                              {[customer.JobTitle, customer.Department].filter(Boolean).join(' • ')}
                            </span>
                          </div>
                        )}
                        {(customer.PersonCompanyName || customer.CompanyName) && (
                          <div className="flex items-center space-x-2">
                            <span>🏢</span>
                            <span>{customer.PersonCompanyName || customer.CompanyName}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col space-y-2">
                      <button
                        onClick={() => handleUseExisting(customer)}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                        title={safeT('customers.updateExisting', 'Update this customer with new data')}
                      >
                        <UserCheck className="h-4 w-4" />
                        <span>{safeT('customers.useExisting', 'Use This')}</span>
                      </button>
                      <button
                        onClick={() => handleViewCustomer(customer.CustomerID)}
                        className="flex items-center space-x-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
                      >
                        <Eye className="h-4 w-4" />
                        <span>{safeT('common.view', 'View')}</span>
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {safeT('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={onContinue}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {safeT('customers.continueAnyway', 'Continue Anyway')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWarningModal;
